import {
  AiBlockType,
  ComparisonValueType,
  ConditionComparator,
  ConditionLogic,
  IntegrationAuthApp,
} from '@prisma/client';
import { v4 as UUID } from 'uuid';

import { DYNAMIC_EDITOR_TAG_EXTENSION_NAME } from '@/components/dynamic-input/constants';
import { frontOperationsDefinition } from '@/levity/integrations/registry/customer-service/providers/front.definition';

import { AppNodeType, FlowNodeType, type FlowTemplate } from '../../types';

const IDs = {
  aiBlocks: {
    classifier: UUID(),
  },
  nodes: {
    frontTrigger: UUID(),
    classifier: UUID(),
    frontAssign: UUID(),
    pathCarrier: UUID(),
    pathShipper: UUID(),
    frontUpdate1: UUID(),
    frontUpdate2: UUID(),
  },
  metadata: {
    carrier: UUID(),
    shipper: UUID(),
    context: UUID(),
  },
};

const FRONT_ICON = {
  alt: 'frontV1 application icon',
  src: '/imgs/icons/frontV1.svg',
};

export const assignRightTeamMemberTemplate: FlowTemplate = {
  name: 'assign-right-team-member',
  description: 'Template for assigning incoming emails to the appropriate team member based on the content.',
  integrations: [IntegrationAuthApp.frontV1],
  title: 'Classify incoming emails and assign team members in Front',
  nodes: [
    {
      id: IDs.nodes.frontTrigger,
      type: FlowNodeType.APP,
      icon: FRONT_ICON.src,
      metadata: {
        type: AppNodeType.TRIGGER,
        integration: IntegrationAuthApp.frontV1,
        operationId: frontOperationsDefinition.NewConversation.id,
      },
    },
    {
      id: IDs.nodes.classifier,
      type: FlowNodeType.AI_BLOCK,
      icon: `/imgs/icons/aiblocks/classify.svg`,
      metadata: {
        aiBlockId: IDs.aiBlocks.classifier,
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: frontOperationsDefinition.NewConversation.id,
              key: 'subject',
              image: FRONT_ICON,
            },
          },
        },
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: frontOperationsDefinition.NewConversation.id,
              key: 'description',
              image: FRONT_ICON,
            },
          },
        },
      ],
    },
    {
      id: IDs.nodes.frontAssign,
      type: FlowNodeType.APP,
      icon: FRONT_ICON.src,
      metadata: {
        type: AppNodeType.TARGET,
        integration: IntegrationAuthApp.frontV1,
        operationId: 'front-add-conversation-tags',
      },
      defaultDynamicInputContent: [
        {
          type: DYNAMIC_EDITOR_TAG_EXTENSION_NAME,
          attrs: {
            dynamicTag: {
              operation: 'llm-classify-with-block-labels',
              key: 'Classification Result',
              image: {
                alt: 'llm application icon',
                src: '/imgs/icons/llm.svg',
              },
            },
          },
        },
      ],
    },
    {
      id: IDs.nodes.pathCarrier,
      type: FlowNodeType.PATH,
      icon: `/imgs/icons/aiblocks/${AiBlockType.classify}.svg`,
      metadata: {},
      conditionGroups: [
        {
          logic: ConditionLogic.AND,
          conditions: [
            {
              logic: ConditionLogic.AND,
              aiBlockMetaDataId: null,
              comparator: ConditionComparator.EQUALS,
              comparisonValueType: ComparisonValueType.STRING,
              comparisonValue: 'carrier',
              sourceNodeId: IDs.nodes.classifier,
              sourceOutputKey: 'Classification Result',
            },
          ],
        },
      ],
    },
    {
      id: IDs.nodes.pathShipper,
      type: FlowNodeType.PATH,
      icon: `/imgs/icons/aiblocks/${AiBlockType.classify}.svg`,
      metadata: {},
      conditionGroups: [
        {
          logic: ConditionLogic.AND,
          conditions: [
            {
              logic: ConditionLogic.AND,
              aiBlockMetaDataId: null,
              comparator: ConditionComparator.EQUALS,
              comparisonValueType: ComparisonValueType.STRING,
              comparisonValue: 'shipper',
              sourceNodeId: IDs.nodes.classifier,
              sourceOutputKey: 'Classification Result',
            },
          ],
        },
      ],
    },
    {
      id: IDs.nodes.frontUpdate1,
      type: FlowNodeType.APP,
      icon: FRONT_ICON.src,
      metadata: {
        type: AppNodeType.TARGET,
        integration: IntegrationAuthApp.frontV1,
        operationId: 'front-update-conversation-assignee',
      },
    },
    {
      id: IDs.nodes.frontUpdate2,
      type: FlowNodeType.APP,
      icon: FRONT_ICON.src,
      metadata: {
        type: AppNodeType.TARGET,
        integration: IntegrationAuthApp.frontV1,
        operationId: 'front-update-conversation-assignee',
      },
    },
  ],
  edges: [
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.frontTrigger,
      targetId: IDs.nodes.classifier,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.classifier,
      targetId: IDs.nodes.frontAssign,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.frontAssign,
      targetId: IDs.nodes.pathCarrier,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.frontAssign,
      targetId: IDs.nodes.pathShipper,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.pathCarrier,
      targetId: IDs.nodes.frontUpdate1,
    },
    {
      sourceHandle: 'source',
      targetHandle: 'target',
      sourceId: IDs.nodes.pathShipper,
      targetId: IDs.nodes.frontUpdate2,
    },
  ],
  aiBlocks: [
    {
      id: IDs.aiBlocks.classifier,
      name: 'Classifier',
      emoji: '🍉',
      type: 'classify',
      metadata: [
        {
          labelSource: 'UserAnnotated',
          key: 'carrier',
          id: UUID(),
          value: 'carrier',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: 'shipper',
          id: UUID(),
          value: 'shipper',
          type: 'AiBlockLabel',
        },
        {
          labelSource: 'UserAnnotated',
          key: '',
          id: UUID(),
          value: 'Determine whether an incoming email is from a carrier or from a shipper.',
          type: 'Context',
        },
      ],
    },
  ],
};
